import { Sparkles, Type, Upload } from "lucide-react";
import { useState, type ReactNode } from "react";

import type { Catalog, Job } from "../../../api/generated/model";
import { Pill } from "../../../ui/Pill";
import classes from "./composer.module.css";
import { ActorBuilder } from "./ActorBuilder";
import { GenerateWithText, actorModels, type ActorFormProps } from "./GenerateWithText";
import type { ComposerPlace } from "./place";
import { UploadActor } from "./UploadActor";

type Mode = "text" | "builder" | "upload";

/** Live's three modes, in its order, words and icons (`ACTION_CONFIG`). */
const MODES: {
  mode: Mode;
  label: string;
  icon: ReactNode;
  form: (props: ActorFormProps) => ReactNode;
}[] = [
  { mode: "text", label: "Generate With Text", icon: <Type size={16} />, form: GenerateWithText },
  { mode: "builder", label: "AI Actor Builder", icon: <Sparkles size={16} />, form: ActorBuilder },
  { mode: "upload", label: "Upload Actor", icon: <Upload size={16} />, form: UploadActor },
];

export interface ActorsComposerProps {
  teamId: string;
  brandId: string;
  catalog: Catalog;
  onStarted: (job: Job) => void;
  /** Talking Head's "Add AI Actor" (live's `actor-generation-shell`); none on the AI Actors page. */
  place?: ComposerPlace;
  /** In a card of its own, not floating (`GlassPanel`'s `inline`, the Pro Studio redesign). */
  inline?: boolean | undefined;
}

/**
 * Live's AI Actors composer (`action-form-panel`, captures 01, 04, 06): its mode pills above a
 * floating panel (Generate With Text first, AI Actor Builder, Upload Actor) and the chosen mode's
 * form, on every tab of the page. Talking Head's "Add AI Actor" shows it in the form's place
 * with its own `place` (issue 238).
 * With no model that makes actors there is no composer.
 */
export function ActorsComposer(props: ActorsComposerProps) {
  const { teamId, brandId, catalog, onStarted, place = {}, inline } = props;
  const models = actorModels(catalog.image_models);
  const [mode, setMode] = useState<Mode>("text");
  const chosen = MODES.find((each) => each.mode === mode) ?? MODES[0]!;
  if (models.length === 0) return null;
  const above = (
    <div className={classes.modes}>
      {MODES.map((each) => (
        <Pill
          key={each.mode}
          label={each.label}
          icon={each.icon}
          active={each.mode === chosen.mode}
          aria-pressed={each.mode === chosen.mode}
          onClick={() => setMode(each.mode)}
        />
      ))}
    </div>
  );
  const Form = chosen.form;
  // Each mode keeps a form of its own, as live's do.
  return (
    <Form
      key={chosen.mode}
      teamId={teamId}
      brandId={brandId}
      models={models}
      above={above}
      onStarted={onStarted}
      place={place}
      inline={inline}
    />
  );
}
